import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, ImagePlus, RefreshCw, Send } from 'lucide-react'
import {
  Badge,
  Button,
  ConfirmDialog,
  FileDropzone,
  Modal,
  MultiCombobox,
  SegmentedControl,
  WorkflowSection,
  NumberStepper,
  SearchInput,
  Select,
  Table,
  UnderlineTabs,
} from '../../components/ui'
import { ScopePicker } from './ScopePicker'
import {
  AuthorizedMediaImage,
  fetchAuthorizedMediaBuffer,
  isInternalApiMediaUrl,
} from '../../shared/media/authorizedMedia'
import { hasPermission } from '../../app/access'
import { useAuthStore } from '../../stores/authStore'
import { useBackgroundJobsStore } from '../../stores/backgroundJobsStore'
import JobProgressModal from '../../components/JobProgressModal'
import {
  contentAssetThumbnailUrl,
  managedFunnelsAPI,
  type ContentAsset,
} from '../content-funnels/managedApi'
import { useContentFunnelOptions } from '../content-funnels/useContentFunnelOptions'
import { useSlideInsertState } from './index'
import { insertError, slideInsertsAPI } from './api'
import {
  INSERT_STATUS_LABELS,
  INSERT_REASON_LABELS,
  INSERT_EVENT_LABELS,
  type InsertPreview,
  type SlideInsert,
  type SlideInsertInput,
  type SlideInsertPrefill,
} from './types'
import { SlideInsertCardsTable } from './SlideInsertCardsTable'

const marketplaceLabels = { wb: 'ВБ', ozon: 'ОЗОН', ym: 'Маркет', site: 'Сайт' } as const
const defaults = (prefill: SlideInsertPrefill): SlideInsertInput => ({
  ...prefill,
  name: '',
  scopeKind: prefill.scopeKind || 'theme',
  marketplaces: prefill.marketplaces || ['wb', 'ozon', 'ym'],
  positionMode: 'end',
  positionIndex: null,
  overflowPolicy: 'skip_card',
  applyToNew: true,
})
const unique = (values: Array<number | undefined>) => [
  ...new Set(values.filter((v): v is number => v !== undefined && v !== null)),
]

export function SlideInsertModalHost() {
  const prefill = useSlideInsertState((s) => s.prefill)
  const close = useSlideInsertState((s) => s.close)
  return prefill ? (
    <SlideInsertModal key={JSON.stringify(prefill)} prefill={prefill} onClose={close} />
  ) : null
}

export function SlideInsertModal({
  prefill,
  onClose,
}: {
  prefill: SlideInsertPrefill
  onClose: () => void
}) {
  const permissions = useAuthStore((s) => s.user?.permissions || [])
  const canRead = hasPermission('ContentFunnel:READ', permissions)
  const canUpdate = hasPermission('ContentFunnel:UPDATE', permissions)
  const canDelete = hasPermission('ContentFunnel:DELETE', permissions)
  const canSend = hasPermission('Card:UPDATE', permissions)
  const references = useContentFunnelOptions(canRead)
  const [input, setInput] = useState<SlideInsertInput>(() => defaults(prefill))
  const [detail, setDetail] = useState<SlideInsert | null>(null)
  const [editing, setEditing] = useState(!prefill.insertId)
  const [assets, setAssets] = useState<ContentAsset[]>([])
  const [assetPicker, setAssetPicker] = useState(false)
  const [assetSearch, setAssetSearch] = useState('')
  const [replace, setReplace] = useState(false)
  const [showUpload, setShowUpload] = useState(false)
  const [imageIndex, setImageIndex] = useState<number | null>(null)
  const [previewResult, setPreview] = useState<InsertPreview | null>(null)
  const [previewResultKey, setPreviewResultKey] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [sampleIndex, setSampleIndex] = useState(0)
  const [showSkipped, setShowSkipped] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [tab, setTab] = useState('cards')
  const [cardStatus, setCardStatus] = useState(prefill.cardStatus || '')
  const [jobId, setJobId] = useState<string | null>(null)
  const [progressOpen, setProgressOpen] = useState(false)
  const [previewJobId, setPreviewJobId] = useState<string | null>(null)
  const previewJobKey = useRef<string | null>(null)
  const [confirm, setConfirm] = useState<'send' | 'disable' | 'delete' | null>(null)
  const [sendIds, setSendIds] = useState<number[] | undefined>()
  const [selectedSendBreakdown, setSelectedSendBreakdown] = useState<SlideInsert['sendBreakdown']>()
  const [recalc, setRecalc] = useState<{ added?: number; removed?: number } | null>(null)
  const job = useBackgroundJobsStore((s) => (jobId ? s.jobs.get(jobId) : undefined))
  const previewJob = useBackgroundJobsStore((s) =>
    previewJobId ? s.jobs.get(previewJobId) : undefined,
  )
  const asset = assets.find((row) => row.id === input.contentAssetId)
  const patch = (value: Partial<SlideInsertInput>) => setInput((old) => ({ ...old, ...value }))

  async function loadDetail(id: number) {
    const data = await slideInsertsAPI.get(id)
    setDetail(data)
    setInput({
      insertId: data.id,
      name: data.name,
      contentAssetId: data.content_asset_id,
      scopeKind: data.scope_kind,
      bundleIds: unique(data.scopes.map((s) => s.bundle_id)),
      themeNumber: data.scopes.find((s) => s.theme_number)?.theme_number,
      marketplaces: [
        ...new Set(
          data.scopes
            .map((s) => s.marketplace)
            .filter((v): v is keyof typeof marketplaceLabels => Boolean(v)),
        ),
      ],
      sellerIds: unique(data.scopes.map((s) => s.seller_id)).length
        ? unique(data.scopes.map((s) => s.seller_id))
        : undefined,
      categoryIds: unique(data.scopes.map((s) => s.category_id)).length
        ? unique(data.scopes.map((s) => s.category_id))
        : undefined,
      positionMode: data.position_mode,
      positionIndex: data.position_index,
      overflowPolicy: data.overflow_policy,
      applyToNew: Boolean(data.apply_to_new),
      revision: data.revision,
    })
  }
  useEffect(() => {
    if (!canRead) return
    let alive = true
    void managedFunnelsAPI
      .listAssets({ kind: 'static_image' })
      .then((data) => {
        if (alive) setAssets(data.filter((a) => a.status === 'ready'))
      })
      .catch((e) => {
        if (alive) setError(insertError(e))
      })
    if (prefill.insertId)
      void loadDetail(prefill.insertId).catch((e) => {
        if (alive) setError(insertError(e))
      })
    return () => {
      alive = false
    }
  }, [prefill.insertId, canRead])
  const validScope =
    input.scopeKind === 'bundle'
      ? Boolean(input.bundleIds?.length)
      : Boolean(
          input.themeNumber &&
          input.marketplaces?.length &&
          input.sellerIds?.length !== 0 &&
          input.categoryIds?.length !== 0,
        )
  const previewKey = JSON.stringify({
    insertId: input.insertId,
    contentAssetId: input.contentAssetId,
    scopeKind: input.scopeKind,
    themeNumber: input.themeNumber,
    bundleIds: input.bundleIds,
    marketplaces: input.marketplaces,
    sellerIds: input.sellerIds,
    categoryIds: input.categoryIds,
    positionMode: input.positionMode,
    positionIndex: input.positionIndex,
    overflowPolicy: input.overflowPolicy,
    revision: input.revision,
  })
  const preview = previewResultKey === previewKey ? previewResult : null
  useEffect(() => {
    setPreview(null)
    setPreviewJobId(null)
    previewJobKey.current = null
    setPreviewLoading(false)
    if (!editing || !input.contentAssetId || !validScope || !canRead) return
    const controller = new AbortController()
    setPreviewLoading(true)
    const timeout = setTimeout(() => {
      void slideInsertsAPI
        .preview(input, controller.signal)
        .then((data) => {
          if (!controller.signal.aborted) {
            if ('jobId' in data) {
              previewJobKey.current = previewKey
              setPreviewJobId(data.jobId)
              return
            }
            setPreviewResultKey(previewKey)
            setPreview(data)
            setPreviewLoading(false)
            setSampleIndex(0)
            setImageIndex(null)
            setError('')
          }
        })
        .catch((e) => {
          if (!controller.signal.aborted) {
            setError(insertError(e))
            setPreviewLoading(false)
          }
        })
    }, 350)
    return () => {
      clearTimeout(timeout)
      controller.abort()
    }
  }, [previewKey, editing, validScope, canRead])
  useEffect(() => {
    if (previewJobKey.current !== previewKey) return
    if (previewJob?.status === 'completed') {
      setPreviewResultKey(previewKey)
      setPreview(previewJob.metadata?.result as InsertPreview)
      setPreviewLoading(false)
      setPreviewJobId(null)
      setSampleIndex(0)
      setImageIndex(null)
      setError('')
    } else if (previewJob && ['failed', 'cancelled', 'stopped'].includes(previewJob.status)) {
      setError(previewJob.error || 'Не удалось пересчитать охват')
      setPreviewLoading(false)
      setPreviewJobId(null)
    }
  }, [previewJob?.status, previewKey])
  useEffect(() => {
    if (job?.status === 'completed' && detail) {
      setJobId(null)
      setEditing(false)
      void loadDetail(detail.id).catch((e) => setError(insertError(e)))
      window.dispatchEvent(new Event('slide-inserts-changed'))
    } else if (job && ['failed', 'cancelled', 'stopped'].includes(job.status)) {
      setError(job.error || 'Задача не завершена. Повторите действие.')
      setJobId(null)
    }
  }, [job?.status, detail?.id])
  function track(id: string) {
    setJobId(id)
    setProgressOpen(true)
    const store = useBackgroundJobsStore.getState()
    if (!store.jobs.has(id))
      store.addJob({
        jobId: id,
        userId: Number(useAuthStore.getState().user?.user_id || 0),
        type: 'slide_insert_apply',
        status: 'pending',
        name: 'Слайд в карточки',
        currentStep: 'Ожидание',
        progress: { current: 0, total: 0, percentage: 0 },
        steps: [],
        startedAt: new Date().toISOString(),
      })
  }
  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError('')
    try {
      await action()
    } catch (e) {
      setError(insertError(e))
    } finally {
      setBusy(false)
    }
  }
  async function upload(file: File | null) {
    if (!file) return
    await run(async () => {
      if (!/image\/(jpeg|png|webp)/.test(file.type) || file.size > 10 * 1024 * 1024)
        throw new Error('Выберите JPG, PNG или WEBP размером до 10 МБ')
      const bitmap = await createImageBitmap(file)
      const width = bitmap.width,
        height = bitmap.height
      bitmap.close()
      if (width < 900 || height < 1200) throw new Error('Минимальный размер — 900×1200 пикселей')
      chooseAsset(await managedFunnelsAPI.uploadStatic(file.name, file))
    })
  }
  async function saveSourceImage() {
    if (!prefill.sourceImageUrl) return
    await run(async () => {
      const source = prefill.sourceImageUrl!
      const response = isInternalApiMediaUrl(source)
        ? new Blob([await fetchAuthorizedMediaBuffer(source)])
        : await fetch(source).then((r) => {
            if (!r.ok) throw new Error('Не удалось загрузить кадр')
            return r.blob()
          })
      const bitmap = await createImageBitmap(response)
      if (bitmap.width < 900 || bitmap.height < 1200) {
        bitmap.close()
        throw new Error('Минимальный размер — 900×1200 пикселей')
      }
      bitmap.close()
      const objectUrl = URL.createObjectURL(response)
      try {
        const img = new Image()
        img.src = objectUrl
        await img.decode()
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        canvas.getContext('2d')!.drawImage(img, 0, 0)
        const blob = await new Promise<Blob>((resolve, reject) =>
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('Не удалось сохранить кадр'))),
            'image/png',
          ),
        )
        if (blob.size > 10 * 1024 * 1024) throw new Error('Картинка превышает 10 МБ')
        chooseAsset(
          await managedFunnelsAPI.uploadStatic(
            'Слайд из карточки',
            new File([blob], 'slide.png', { type: 'image/png' }),
          ),
        )
      } finally {
        URL.revokeObjectURL(objectUrl)
      }
    })
  }
  function chooseAsset(next: ContentAsset) {
    if ((next.width || 0) < 900 || (next.height || 0) < 1200) {
      setError('Минимальный размер — 900×1200 пикселей')
      setAssetPicker(false)
      setReplace(false)
      return
    }
    setAssets((old) => (old.some((a) => a.id === next.id) ? old : [...old, next]))
    setAssetPicker(false)
    if (replace && detail) {
      setReplace(false)
      void run(async () => {
        const result = await slideInsertsAPI.action(detail.id, 'replace-image', {
          contentAssetId: next.id,
        })
        if (result.jobId) track(result.jobId)
      })
      return
    }
    patch({ contentAssetId: next.id, name: input.name || next.name })
  }
  async function apply() {
    if (
      !preview?.eligible ||
      previewLoading ||
      !validScope ||
      !input.name.trim() ||
      !canUpdate ||
      busy ||
      jobId ||
      assetInvalid
    )
      return
    await run(async () => {
      if (detail) {
        const result = await slideInsertsAPI.update(detail.id, {
          ...input,
          revision: detail.revision,
        })
        if ('jobId' in result) {
          track(result.jobId)
          return
        }
        setDetail(result)
        if (result.status !== 'draft') {
          setEditing(false)
          return
        }
        const applied = await slideInsertsAPI.action(result.id, 'apply')
        if (applied.jobId) track(applied.jobId)
      } else {
        const created = await slideInsertsAPI.create(input)
        setDetail(created)
        const result = await slideInsertsAPI.action(created.id, 'apply')
        if (result.jobId) track(result.jobId)
      }
    })
  }
  const sample = preview?.samples?.[sampleIndex]
  const sellers = references.sellers.filter((s) =>
    input.marketplaces?.includes(s.marketplace as keyof typeof marketplaceLabels),
  )
  const categoryCounts = preview?.facets?.categories
  const categoryOptions = new Map<number, string>(
    references.categories.map((c) => [c.category_id, `${c.prefix} ${c.description}`]),
  )
  categoryCounts?.forEach((c) => {
    if (!categoryOptions.has(c.id)) categoryOptions.set(c.id, c.name)
  })
  input.categoryIds?.forEach((id) => {
    if (!categoryOptions.has(id)) categoryOptions.set(id, `Носитель ${id}`)
  })
  const categories = [...categoryOptions].map(([id, name]) => ({
    id,
    name,
    count: categoryCounts ? categoryCounts.find((c) => c.id === id)?.count || 0 : undefined,
  }))
  const activeEdit = editing && detail?.status === 'active'
  const mixedWarning =
    preview?.mixedChanges || detail?.mixedChanges
      ? `У ${preview?.mixedChanges || detail?.mixedChanges} карточек уйдут и другие неотправленные изменения фото.`
      : 'При отправке могут уйти другие неотправленные изменения фото карточки.'
  const assetInvalid = Boolean(asset && ((asset.width || 0) < 900 || (asset.height || 0) < 1200))
  const imageBar = (
    <div className="space-y-3 rounded-lg border border-gray-200 p-3 dark:border-zinc-700">
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 sm:flex sm:flex-wrap">
        {input.contentAssetId && (
          <AuthorizedMediaImage
            src={contentAssetThumbnailUrl(input.contentAssetId)}
            alt={asset?.name || 'Выбранный слайд'}
            className="h-14 w-11 shrink-0 rounded object-contain"
          />
        )}
        <div className={`min-w-0 flex-1 ${input.contentAssetId ? '' : 'col-span-2'}`}>
          <p className="truncate text-sm font-medium">
            {asset?.name ||
              (input.contentAssetId
                ? `Ресурс ${input.contentAssetId}`
                : 'Выберите изображение слайда')}
          </p>
          {asset && (
            <p className="text-xs text-gray-500 dark:text-zinc-400">
              {asset.width}×{asset.height} · из библиотеки
            </p>
          )}
        </div>
        <Button
          size="sm"
          variant="secondary"
          className="col-span-2"
          icon={<ImagePlus size={16} />}
          disabled={activeEdit || busy}
          onClick={() => setAssetPicker(true)}
        >
          Выбрать из библиотеки
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="col-span-2"
          disabled={activeEdit || busy}
          onClick={() => setShowUpload((v) => !v)}
        >
          Загрузить
        </Button>
      </div>
      <div hidden={Boolean(input.contentAssetId) && !showUpload}>
        <FileDropzone
          label="Загрузить слайд"
          accept="image/jpeg,image/png,image/webp"
          helperText="До 10 МБ, минимум 900×1200"
          disabled={activeEdit || busy}
          onChange={(file) => void upload(file)}
        />
      </div>
      {asset?.validation_warnings?.map((w) => (
        <p key={w} className="text-xs text-amber-700 dark:text-amber-400">
          {w}
        </p>
      ))}
      {asset?.width && asset?.height && Math.abs(asset.width / asset.height - 0.75) > 0.02 ? (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          ВБ покажет с полями: пропорция отличается от 3:4
        </p>
      ) : null}
      {activeEdit && (
        <div className="text-sm">
          <p>Для смены слайда используйте «Заменить картинку» в карточке вставки.</p>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
            Вернуться к карточке вставки
          </Button>
        </div>
      )}
      {assetInvalid && (
        <p role="alert" className="text-red-600 dark:text-red-400">
          Минимальный размер — 900×1200 пикселей
        </p>
      )}
      {prefill.sourceImageUrl && !input.contentAssetId && (
        <p className="text-sm">
          Этот кадр ещё не в библиотеке.{' '}
          <Button
            size="sm"
            variant="secondary"
            loading={busy}
            onClick={() => void saveSourceImage()}
          >
            Сохранить в библиотеку
          </Button>
        </p>
      )}
    </div>
  )
  const actualSellers = [
    ...sellers.map((s) => ({
      id: Number(s.id),
      label: `${s.name} · ID ${s.id} · ${marketplaceLabels[s.marketplace as keyof typeof marketplaceLabels]} · ${preview?.facets?.sellers?.find((f) => f.id === Number(s.id))?.count ?? '—'}`,
    })),
    ...(input.sellerIds || [])
      .filter((id) => !sellers.some((s) => Number(s.id) === id))
      .map((id) => ({ id, label: `Кабинет ${id}` })),
  ]
  const actualCategories = categories.map((c) => ({
    id: c.id,
    label: `${c.name} · ID ${c.id} · ${c.count ?? '—'}`,
  }))
  const compositionUrl =
    imageIndex !== null
      ? sample?.urls[imageIndex]
      : sample?.insertUrl ||
        (input.contentAssetId ? contentAssetThumbnailUrl(input.contentAssetId) : undefined)
  const placeControls = (
    <div className="space-y-3 border-t border-gray-200 pt-3 dark:border-zinc-700">
      <label className="block text-sm font-medium">
        Куда вставить
        <Select
          aria-label="Место в галерее"
          value={input.positionMode}
          onChange={(e) =>
            patch({
              positionMode: e.target.value as 'end' | 'index',
              positionIndex: e.target.value === 'index' ? 3 : null,
            })
          }
        >
          <option value="end">В конец галереи</option>
          <option value="index">На место №</option>
        </Select>
      </label>
      {input.positionMode === 'index' && (
        <NumberStepper
          label="Номер места"
          min={2}
          max={30}
          value={input.positionIndex || 3}
          onChange={(positionIndex) => patch({ positionIndex })}
        />
      )}
      <p className="text-xs text-gray-500 dark:text-zinc-400">
        Главное фото — товар, его не заменяем
      </p>
      <WorkflowSection
        title="Дополнительно"
        collapsible
        summary={`${input.applyToNew ? 'Добавлять в новые карточки' : 'Только существующие карточки'} · ${input.overflowPolicy === 'skip_card' ? 'пропуск заполненных галерей' : 'убрать последний слайд'}`}
      >
        <label className="mb-3 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={input.applyToNew}
            onChange={(e) => patch({ applyToNew: e.target.checked })}
          />
          Добавлять в новые карточки {input.scopeKind === 'bundle' ? 'склейки' : 'тематики'}
        </label>
        <label className="block text-sm">
          Если галерея заполнена
          <Select
            aria-label="Если не влезает"
            value={input.overflowPolicy}
            onChange={(e) => patch({ overflowPolicy: e.target.value as 'skip_card' | 'drop_last' })}
          >
            <option value="skip_card">Пропустить карточку</option>
            <option value="drop_last">Убрать последний слайд карточки</option>
          </Select>
        </label>
      </WorkflowSection>
    </div>
  )
  const workspace = (
    <>
      <label className="block text-sm font-medium">
        Название вставки
        <input
          aria-label="Название вставки"
          className="mt-1 block w-full rounded-md border border-gray-300 bg-white p-2 dark:border-zinc-700 dark:bg-zinc-900"
          value={input.name}
          maxLength={255}
          disabled={!canUpdate || busy || Boolean(jobId)}
          onChange={(e) => patch({ name: e.target.value })}
        />
      </label>
      <fieldset disabled={!canUpdate || busy || Boolean(jobId)}>{imageBar}</fieldset>
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <fieldset disabled={!canUpdate || busy || Boolean(jobId)} className="min-w-0 space-y-4">
          <h3 className="text-sm font-semibold">Какие карточки изменить</h3>
          <SegmentedControl
            aria-label="Вид охвата"
            fullWidth
            value={input.scopeKind}
            options={[
              { value: 'theme', label: 'Все карточки тематики' },
              { value: 'bundle', label: 'Выбранные склейки' },
            ]}
            onChange={(scopeKind) => {
              if (scopeKind === input.scopeKind) return
              patch({
                scopeKind,
                bundleIds: undefined,
                themeNumber: undefined,
                sellerIds: undefined,
                categoryIds: undefined,
              })
            }}
          />
          <ScopePicker
            kind={input.scopeKind}
            bundleIds={input.bundleIds}
            themeNumber={input.themeNumber}
            onChange={(value) =>
              patch({
                ...value,
                ...(input.scopeKind === 'theme' && value.themeNumber !== input.themeNumber
                  ? { sellerIds: undefined, categoryIds: undefined }
                  : {}),
              })
            }
            sellers={references.sellers}
            categories={categories}
            disabled={!canUpdate || busy || Boolean(jobId)}
          />
          {input.scopeKind === 'theme' && (
            <WorkflowSection
              title="Ограничить карточки выбранной тематики"
              collapsible
              defaultOpen={
                input.sellerIds !== undefined ||
                input.categoryIds !== undefined ||
                input.marketplaces?.length !== 3 ||
                !['wb', 'ozon', 'ym'].every((mp) =>
                  input.marketplaces?.includes(mp as keyof typeof marketplaceLabels),
                )
              }
              attention={
                input.marketplaces?.length === 0 ||
                input.sellerIds?.length === 0 ||
                input.categoryIds?.length === 0
              }
              description="Эти настройки меняют реальный охват. Числа — из предпросмотра карточек."
              summary={`${input.marketplaces?.map((mp) => marketplaceLabels[mp]).join(', ') || 'Площадки не выбраны'} · ${input.sellerIds === undefined ? 'все кабинеты' : `кабинетов: ${input.sellerIds.length}`} · ${input.categoryIds === undefined ? 'все носители' : `носителей: ${input.categoryIds.length}`} · ${preview?.total ?? '—'} карточек после ограничений`}
            >
              <div className="space-y-3">
                <fieldset>
                  <legend className="mb-2 text-xs font-medium">Площадки охвата</legend>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(marketplaceLabels).map(([key, label]) => (
                      <label key={key} className="text-sm">
                        <input
                          type="checkbox"
                          checked={
                            input.marketplaces?.includes(key as keyof typeof marketplaceLabels) ||
                            false
                          }
                          onChange={(e) =>
                            patch({
                              marketplaces: e.target.checked
                                ? [
                                    ...(input.marketplaces || []),
                                    key as keyof typeof marketplaceLabels,
                                  ]
                                : input.marketplaces?.filter((mp) => mp !== key),
                              sellerIds: undefined,
                            })
                          }
                        />{' '}
                        {label}
                      </label>
                    ))}
                  </div>
                </fieldset>
                <div>
                  <label className="mb-1 block text-xs font-medium">
                    Кабинеты охвата{' '}
                    {input.sellerIds === undefined
                      ? '· все'
                      : `· выбрано ${input.sellerIds.length}`}
                  </label>
                  <MultiCombobox
                    aria-label="Кабинеты охвата"
                    values={(input.sellerIds === undefined
                      ? actualSellers
                      : actualSellers.filter((s) => input.sellerIds?.includes(s.id))
                    ).map((s) => s.label)}
                    options={actualSellers.map((s) => s.label)}
                    onChange={(values) =>
                      patch({
                        sellerIds: actualSellers
                          .filter((s) => values.includes(s.label))
                          .map((s) => s.id),
                      })
                    }
                    placeholder="Выберите кабинеты"
                  />
                  <Button size="sm" variant="ghost" onClick={() => patch({ sellerIds: undefined })}>
                    Все кабинеты охвата
                  </Button>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">
                    Носители охвата{' '}
                    {input.categoryIds === undefined
                      ? '· все'
                      : `· выбрано ${input.categoryIds.length}`}
                  </label>
                  <MultiCombobox
                    aria-label="Носители охвата"
                    values={(input.categoryIds === undefined
                      ? actualCategories
                      : actualCategories.filter((c) => input.categoryIds?.includes(c.id))
                    ).map((c) => c.label)}
                    options={actualCategories.map((c) => c.label)}
                    onChange={(values) =>
                      patch({
                        categoryIds: actualCategories
                          .filter((c) => values.includes(c.label))
                          .map((c) => c.id),
                      })
                    }
                    placeholder="Выберите носители"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => patch({ categoryIds: undefined })}
                  >
                    Все носители охвата
                  </Button>
                </div>
                {!validScope && (
                  <p role="alert" className="text-sm text-amber-700 dark:text-amber-400">
                    Для охвата выберите тематику, площадку, кабинет и носитель. Пустой выбор не
                    означает «все».
                  </p>
                )}
              </div>
            </WorkflowSection>
          )}
        </fieldset>
        <aside
          className="min-w-0 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-zinc-700 dark:bg-zinc-800/40 lg:sticky lg:top-0"
          aria-label="Предпросмотр результата"
        >
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Как будет в карточке</h3>
            {sample && (
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Предыдущий образец"
                  disabled={!sampleIndex}
                  onClick={() => {
                    setSampleIndex((n) => n - 1)
                    setImageIndex(null)
                  }}
                >
                  <ChevronLeft size={16} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Следующий образец"
                  disabled={sampleIndex >= preview!.samples.length - 1}
                  onClick={() => {
                    setSampleIndex((n) => n + 1)
                    setImageIndex(null)
                  }}
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            )}
          </div>
          {sample && (
            <p className="text-xs">
              <code className="font-mono">{sample.article}</code> ·{' '}
              {marketplaceLabels[sample.marketplace]} · {sample.seller_name}
            </p>
          )}
          <div className="flex min-h-48 items-center justify-center rounded-lg border border-gray-200 bg-white p-2 dark:border-zinc-700 dark:bg-zinc-900">
            {compositionUrl ? (
              <AuthorizedMediaImage
                src={compositionUrl}
                alt={
                  imageIndex === null
                    ? 'Предпросмотр нового слайда'
                    : `Предпросмотр места ${imageIndex + 1}`
                }
                className="max-h-64 w-full object-contain"
              />
            ) : (
              <p className="p-8 text-center text-sm text-gray-500 dark:text-zinc-400">
                Выберите изображение — здесь появится слайд и порядок галереи.
              </p>
            )}
          </div>
          {sample && (
            <>
              <div className="flex gap-2 overflow-x-auto pb-1" aria-label="Порядок слайдов">
                {sample.urls.map((url, index) => (
                  <Button
                    key={`${url}:${index}`}
                    size="sm"
                    variant="ghost"
                    className={`h-auto w-16 shrink-0 flex-col p-1 ${url === sample.insertUrl ? 'ring-2 ring-indigo-500' : ''}`}
                    aria-pressed={
                      imageIndex === index || (imageIndex === null && url === sample.insertUrl)
                    }
                    onClick={() => setImageIndex(index)}
                  >
                    <AuthorizedMediaImage
                      src={url}
                      alt={`Место ${index + 1}`}
                      className="h-16 w-12 object-contain"
                    />
                    <span className="text-xs">
                      {index + 1}
                      {url === sample.insertUrl ? ' · новый' : ''}
                    </span>
                  </Button>
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-zinc-400">
                {sample.urls.length}/{sample.limit} слайдов · образец {sampleIndex + 1}/
                {preview!.samples.length}
              </p>
            </>
          )}
          <fieldset disabled={!canUpdate || busy || Boolean(jobId)}>{placeControls}</fieldset>
          <div
            aria-live="polite"
            className="space-y-2 border-t border-gray-200 pt-3 text-sm dark:border-zinc-700"
          >
            {previewLoading ? (
              <p role="status">Пересчитываем охват…</p>
            ) : !preview ? (
              <p>Выберите слайд и охват</p>
            ) : (
              <>
                <p className="font-semibold">Карточек: {preview.total}</p>
                <p>Черновики — сразу: {preview.drafts}</p>
                <p>На площадке — отправка: {preview.pending}</p>
                <Button variant="ghost" size="sm" onClick={() => setShowSkipped((old) => !old)}>
                  Не попадут: {preview.skipped} · подробнее
                </Button>
                {!preview.eligible && <p>В выбранном охвате нет карточек</p>}
                {preview.mixedChanges > 0 && (
                  <p className="text-xs text-amber-700 dark:text-amber-400">{mixedWarning}</p>
                )}
              </>
            )}
          </div>
        </aside>
      </div>
      {showSkipped && preview && (
        <WorkflowSection title="Почему карточки не попадут" defaultOpen>
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              void navigator.clipboard.writeText(
                preview.cards
                  .filter((c) => c.reason)
                  .map((c) => c.article)
                  .join('\n'),
              )
            }
          >
            Копировать артикулы
          </Button>
          <Table
            data={preview.cards.filter((c) => c.reason)}
            keyExtractor={(c) => c.card_id}
            columns={[
              { key: 'article', header: 'Артикул', render: (_, c) => <code>{c.article}</code> },
              { key: 'seller_name', header: 'Кабинет' },
              {
                key: 'reason',
                header: 'Причина',
                render: (_, row) =>
                  row.reason ? INSERT_REASON_LABELS[row.reason] || row.reason : '—',
              },
            ]}
          />
        </WorkflowSection>
      )}
    </>
  )
  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        title={detail ? `Слайд в карточки: ${detail.name}` : 'Слайд в карточки'}
        size="xxl"
        footer={
          <>
            {editing && (
              <div className="mr-auto min-w-0 text-xs">
                <strong className="block">
                  {!input.contentAssetId || !validScope
                    ? 'Выберите слайд и охват'
                    : previewLoading || !preview
                      ? 'Пересчитываем охват…'
                      : `${preview.eligible} карточек готовы к применению`}
                </strong>
                <span className="block text-gray-500 dark:text-zinc-400">
                  Черновики: {preview?.drafts || 0} · ждут отправки: {preview?.pending || 0} ·
                  пропуски: {preview?.skipped || 0}
                </span>
                <span className="block text-gray-500 dark:text-zinc-400">
                  На площадки отправляются отдельным действием
                </span>
              </div>
            )}
            <Button variant="secondary" onClick={onClose}>
              Закрыть
            </Button>
            {editing && canUpdate && (
              <Button
                loading={busy}
                disabled={
                  Boolean(jobId) ||
                  !input.name.trim() ||
                  !preview?.eligible ||
                  previewLoading ||
                  assetInvalid
                }
                onClick={() => void apply()}
              >
                Применить к {preview?.eligible || 0} карточкам
              </Button>
            )}
          </>
        }
      >
        {!canRead ? (
          <p>Нет права на просмотр контент-воронок.</p>
        ) : (
          <div className="space-y-4 text-gray-900 dark:text-zinc-100">
            {error && (
              <p
                role="alert"
                className="rounded border border-red-200 p-3 text-sm text-red-700 dark:border-red-900 dark:text-red-400"
              >
                {error}
              </p>
            )}
            {editing
              ? workspace
              : detail && (
                  <>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge>{INSERT_STATUS_LABELS[detail.status]}</Badge>
                      <span>
                        {detail.position_mode === 'end'
                          ? 'В конец'
                          : `Место ${detail.position_index}`}{' '}
                        ·{' '}
                        {detail.scope_kind === 'bundle'
                          ? `Склейки: ${unique(detail.scopes.map((s) => s.bundle_id)).join(', ')}`
                          : `Тематика ${detail.scopes.find((s) => s.theme_number)?.theme_number}`}
                      </span>
                      {canUpdate && !jobId && ['active', 'draft'].includes(detail.status) && (
                        <>
                          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
                            Изменить
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setReplace(true)
                              setAssetPicker(true)
                            }}
                          >
                            Заменить картинку
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            icon={<RefreshCw size={16} />}
                            onClick={() =>
                              void run(async () =>
                                setRecalc(
                                  await slideInsertsAPI.action(detail.id, 'recalc', {
                                    dry_run: true,
                                  }),
                                ),
                              )
                            }
                          >
                            Пересчитать охват
                          </Button>
                        </>
                      )}
                      <details>
                        <summary className="cursor-pointer text-sm">Ещё</summary>
                        {canUpdate && !jobId && detail.status === 'active' && (
                          <Button size="sm" variant="danger" onClick={() => setConfirm('disable')}>
                            Снять из всех карточек
                          </Button>
                        )}
                        {canDelete && !jobId && ['draft', 'archived'].includes(detail.status) && (
                          <Button size="sm" variant="danger" onClick={() => setConfirm('delete')}>
                            Удалить
                          </Button>
                        )}
                      </details>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(['draft', 'pending', 'sent', 'skipped'] as const).map((state) => (
                        <Button
                          key={state}
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setCardStatus(state)
                            setTab('cards')
                          }}
                        >
                          {state === 'draft'
                            ? 'Черновики обновлены'
                            : state === 'pending'
                              ? 'Ждут отправки'
                              : state === 'sent'
                                ? 'Отправлено'
                                : 'Пропуск'}
                          : {detail.counts?.[state] || 0}
                        </Button>
                      ))}
                    </div>
                    {detail.counts?.pending > 0 &&
                      (canSend ? (
                        <Button
                          icon={<Send size={16} />}
                          disabled={detail.sendEnabled === false || Boolean(jobId)}
                          onClick={() => {
                            setSendIds(undefined)
                            setConfirm('send')
                          }}
                        >
                          Отправить {detail.counts.pending} карточек на площадки…
                        </Button>
                      ) : (
                        <p className="text-sm">Отправит менеджер карточек</p>
                      ))}
                    {detail.sendEnabled === false && (
                      <p className="text-sm">Отправка на площадки отключена.</p>
                    )}
                    <UnderlineTabs
                      tabs={[
                        { key: 'cards', label: 'Карточки' },
                        { key: 'history', label: 'История' },
                      ]}
                      active={tab}
                      onChange={setTab}
                    />
                    {tab === 'cards' ? (
                      <SlideInsertCardsTable
                        insertId={detail.id}
                        revision={detail.revision}
                        initialStatus={cardStatus}
                        canUpdate={canUpdate && !jobId && !busy}
                        canSend={canSend && !jobId && !busy && detail.sendEnabled !== false}
                        onJob={track}
                        onSend={(ids, rows) => {
                          setSendIds(ids)
                          const groups = new Map<
                            string,
                            NonNullable<SlideInsert['sendBreakdown']>[number]
                          >()
                          rows.forEach((row) => {
                            const key = `${row.marketplace}:${row.seller_id}`
                            const existing = groups.get(key)
                            if (existing) existing.count++
                            else
                              groups.set(key, {
                                marketplace: row.marketplace,
                                seller_id: row.seller_id,
                                seller_name: row.seller_name,
                                count: 1,
                              })
                          })
                          setSelectedSendBreakdown([...groups.values()])
                          setConfirm('send')
                        }}
                      />
                    ) : (
                      <Table
                        data={detail.events || []}
                        keyExtractor={(row) => row.id}
                        columns={[
                          { key: 'created_at', header: 'Дата' },
                          {
                            key: 'event_type',
                            header: 'Событие',
                            render: (_, row) =>
                              row.message ||
                              INSERT_EVENT_LABELS[row.event_type || row.type || ''] ||
                              row.event_type ||
                              row.type,
                          },
                          { key: 'user_name', header: 'Автор' },
                        ]}
                      />
                    )}
                  </>
                )}
          </div>
        )}
      </Modal>
      <Modal
        isOpen={assetPicker}
        onClose={() => {
          setAssetPicker(false)
          setReplace(false)
        }}
        title="Выбрать слайд из библиотеки"
        size="lg"
      >
        <SearchInput value={assetSearch} onChange={setAssetSearch} placeholder="Поиск картинки" />
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {assets
            .filter((a) => a.name.toLowerCase().includes(assetSearch.toLowerCase()))
            .map((a) => (
              <Button
                key={a.id}
                variant="secondary"
                className="h-auto flex-col"
                onClick={() => chooseAsset(a)}
              >
                <AuthorizedMediaImage
                  src={contentAssetThumbnailUrl(a.id)}
                  alt={a.name}
                  className="h-28 w-20 object-contain"
                />
                <span className="max-w-full truncate text-xs">{a.name}</span>
              </Button>
            ))}
        </div>
      </Modal>
      <ConfirmDialog
        isOpen={Boolean(confirm)}
        loading={busy}
        danger={confirm !== 'send'}
        title={
          confirm === 'send'
            ? 'Отправить карточки на площадки?'
            : confirm === 'disable'
              ? 'Снять вставку?'
              : 'Удалить вставку?'
        }
        message={
          confirm === 'send' ? (
            <div className="space-y-2">
              <p>Карточек: {sendIds?.length || detail?.counts?.pending || 0}.</p>
              {(sendIds ? selectedSendBreakdown : detail?.sendBreakdown)?.map((group) => (
                <p key={`${group.marketplace}:${group.seller_id}`}>
                  {marketplaceLabels[group.marketplace]} ·{' '}
                  {group.seller_name || `Кабинет ${group.seller_id}`}: {group.count}
                </p>
              ))}
              <p>
                Площадки:{' '}
                {[...new Set(detail?.scopes.map((s) => s.marketplace).filter(Boolean))]
                  .map((mp) => marketplaceLabels[mp as keyof typeof marketplaceLabels])
                  .join(', ') || 'по охвату вставки'}
                .
              </p>
              <p>{mixedWarning}</p>
              <p>ОЗОН: смена фото может отправить карточку на повторную модерацию.</p>
            </div>
          ) : (
            `Будут затронуты ${detail?.counts?.total || 0} карточек. ${confirm === 'disable' ? 'Выгруженные карточки будут ждать отдельной отправки на снятие.' : 'Удаление доступно только для черновика или архива.'}`
          )
        }
        onClose={() => setConfirm(null)}
        onConfirm={() =>
          void run(async () => {
            if (!detail || !confirm) return
            if (confirm === 'delete') {
              await slideInsertsAPI.delete(detail.id)
              window.dispatchEvent(new Event('slide-inserts-changed'))
              onClose()
            } else {
              const result = await slideInsertsAPI.action(
                detail.id,
                confirm,
                confirm === 'send' ? { cardIds: sendIds, publish_confirmed: true } : {},
              )
              if (result.jobId) track(result.jobId)
            }
            setConfirm(null)
          })
        }
      />
      <ConfirmDialog
        isOpen={Boolean(recalc)}
        title="Применить пересчёт охвата?"
        message={`Добавятся: ${recalc?.added || 0}. Уйдут: ${recalc?.removed || 0}. Отправка на площадки запускается отдельно.`}
        onClose={() => setRecalc(null)}
        onConfirm={() =>
          void run(async () => {
            if (!detail) return
            const result = await slideInsertsAPI.action(detail.id, 'recalc', { dry_run: false })
            if (result.jobId) track(result.jobId)
            setRecalc(null)
          })
        }
      />
      {jobId && progressOpen && (
        <JobProgressModal job={job || null} isOpen onClose={() => setProgressOpen(false)} />
      )}
    </>
  )
}
